import { isPublicSimulationRuntime } from './publicSimulationRuntime';
type ClerkTokenOptions = {
  template?: string;
  [key: string]: unknown;
};

type GetTokenFn = (opts?: ClerkTokenOptions) => Promise<string>;

let clerkGetToken: GetTokenFn | null = null;
const defaultJwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE;
const preferredTokenRetryCount = 5;
const preferredTokenRetryDelayMs = 150;

const buildTokenOptions = (opts?: Record<string, unknown>) => {
  if (!defaultJwtTemplate) {
    return opts;
  }
  if (opts && Object.prototype.hasOwnProperty.call(opts, 'template')) {
    return opts;
  }
  return { ...(opts ?? {}), template: defaultJwtTemplate };
};

export function registerClerkGetToken(fn: GetTokenFn) {
  clerkGetToken = fn;
}

const isMissingTemplateError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return message.includes('No JWT template exists with name:');
};

const hasTemplateOption = (opts?: ClerkTokenOptions) => {
  return Boolean(opts?.template && String(opts.template).trim().length > 0);
};

export async function getClerkToken(opts?: ClerkTokenOptions): Promise<string | null> {
  if (!clerkGetToken) return null;
  const resolvedOpts = buildTokenOptions(opts);
  try {
    const token = await clerkGetToken(resolvedOpts);
    return token;
  } catch (e) {
    if (hasTemplateOption(resolvedOpts) && isMissingTemplateError(e)) {
      try {
        const fallbackToken = await clerkGetToken(opts);
        return fallbackToken;
      } catch (fallbackError) {
        console.warn('clerkTokenProvider: error getting token after template fallback', fallbackError);
        return null;
      }
    }
    console.warn('clerkTokenProvider: error getting token', e);
    return null;
  }
}

export function clearClerkGetToken() {
  clerkGetToken = null;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getStoredAccessToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedToken = localStorage.getItem('accessToken');
  const normalizedToken = storedToken?.trim() ?? '';
  if (
    normalizedToken.length === 0 ||
    normalizedToken === 'null' ||
    normalizedToken === 'undefined'
  ) {
    return null;
  }

  return normalizedToken;
};

const shouldRetryPreferredTokenLookup = () => {
  if (clerkGetToken) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return localStorage.getItem('clerk_is_signed_in') === 'true';
};

// UPT-1932 : fraîcheur du jeton avant un appel qui ne doit PAS échouer (ex. la
// sauvegarde automatique d'un questionnaire). On ne duplique pas la chaîne
// d'obtention du jeton : on repasse par getPreferredAccessToken (Clerk →
// localStorage de repli, template JWT appliqué par buildTokenOptions), en lui
// passant simplement `skipCache: true` quand le jeton courant est sur le point
// d'expirer. Clerk met alors le jeton neuf dans son propre cache, donc
// l'intercepteur axios de ClientProviders posera le bon en-tête Authorization.
const jwtPartCount = 3;
const base64PadSize = 4;
const secondsToMilliseconds = 1000;

/** Expiration (ms epoch) portée par la claim `exp` d'un JWT, `null` si illisible. */
export function getAccessTokenExpiryMs(token: string | null | undefined): number | null {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== jwtPartCount) return null;
  if (typeof atob !== 'function') return null;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = (base64PadSize - (normalized.length % base64PadSize)) % base64PadSize;
    const payload = JSON.parse(atob(normalized + '='.repeat(padding))) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * secondsToMilliseconds : null;
  } catch {
    return null;
  }
}

export type FreshTokenOptions = {
  /** Marge avant expiration en deçà de laquelle on force un jeton neuf (défaut 60 s). */
  marginMs?: number;
  /** Force le rafraîchissement sans regarder l'expiration (keep-alive, retry après 401). */
  force?: boolean;
};

const defaultFreshTokenMarginMs = 60000;

/**
 * Jeton d'accès garanti valide encore `marginMs`. Renvoie `null` comme
 * getPreferredAccessToken quand aucune session n'est disponible — l'appelant
 * laisse alors partir la requête, qui se soldera par un 401 explicite.
 */
export async function ensureFreshAccessToken(options?: FreshTokenOptions): Promise<string | null> {
  const marginMs = options?.marginMs ?? defaultFreshTokenMarginMs;

  if (options?.force !== true) {
    const currentToken = await getPreferredAccessToken();
    if (currentToken) {
      const expiryMs = getAccessTokenExpiryMs(currentToken);
      // Jeton opaque (non JWT) : rien à arbitrer, on le garde tel quel.
      if (expiryMs === null || expiryMs - Date.now() > marginMs) {
        return currentToken;
      }
    }
  }

  // getPreferredAccessToken retombe déjà sur le jeton stocké si Clerk échoue.
  return getPreferredAccessToken({ skipCache: true });
}

export async function getPreferredAccessToken(opts?: ClerkTokenOptions): Promise<string | null> {
  if (isPublicSimulationRuntime()) return null;
  const attempts = shouldRetryPreferredTokenLookup() ? preferredTokenRetryCount : 1;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const clerkToken = await getClerkToken(opts);
    if (clerkToken) {
      return clerkToken;
    }

    const storedAccessToken = getStoredAccessToken();
    if (storedAccessToken) {
      return storedAccessToken;
    }

    if (attempt < attempts - 1) {
      await delay(preferredTokenRetryDelayMs);
    }
  }

  return null;
}
